<template>
  <div class="verify-link d-flex justify-center">
    <div class="verify-link__area d-flex justify-center">
      <div class="language-switcher">
        <Button
          block
          dark
          tabindex="1"
          height="30"
          :class="{ 'active-language': currentLang === 'en' }"
          @click.native="updateLanguage('en')">
          English
        </Button>
        <Button
          block
          dark
          tabindex="2"
          height="30"
          :class="{ 'active-language': currentLang === 'es' }"
          @click.native="updateLanguage('es')">
          Spanish
        </Button>
      </div>
      <div class="verify-link__area-wrapper d-flex flex-column">
        <div v-if="responseResult" class="logo d-flex align-center" style="column-gap: 10px;">
          <Img :src="responseResult.logoSmall" width="97" />
          <div style="width: 70%">
            <p class="mb-0" style="font-weight: 600; font-size: 25px;">{{ responseResult.practiceName }}</p>
            <p class="mb-0">{{ responseResult.practiceTitle }}</p>
          </div>
        </div>
        <div v-else class=" d-flex align-center">
          <Img :src="getImgWithExt(['images', 'logo_drawer', 'svg'])" width="172" />
        </div>
        <div class="verify-link__body flex-grow-1">
          <template v-if="confirmationSuccess === true">
            <img class="spinner rotating" src="@/assets/images/spinner.png" :alt="String($t('alt.almost_done'))" />
            <p class="subtitle">{{ $t('loginForm.patientVerification') }}</p>
            <p class="title">{{ $t('loginForm.niceAlmostDone') }}</p>
          </template>
          <template v-if="confirmationSuccess === false">
            <img class="error-icon" src="@/assets/images/attention.png" :alt="String($t('alt.attention'))" />
            <h2 class="registered__title mb-5 mt-6">Error</h2>
<!--            <p class="mb-0" style="color: #2D3748; font-size: 16px; font-weight: 400">Problem with confirmation reference</p>-->
            <p class="mb-0" style="color: #2D3748; font-size: 16px; font-weight: 400">{{ errorMessage }}</p>
            <Link class="back-to-login" color="#A0AEC0" to="/sign-in">{{ $t('loginForm.backToLogin') }}</Link>
          </template>
        </div>
      </div>
    </div>
    <div class="bg-area">
      <img v-if="confirmationSuccess === true" class="bg-image" src="@/assets/images/confirm-bg.png" :alt="String($t('alt.confirm_image'))" />
      <img v-if="confirmationSuccess === false" class="bg-image" src="@/assets/images/error-bg.png" :alt="String($t('alt.error_image'))" />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Button from '@/components/elements/Button'
import Image from '@/mixins/Image'
import Img from '@/components/elements/Img'
import Link from '@/components/elements/Link'

export default {
  name: 'verify-link',
  mixins: [Image],
  components: {Button, Img, Link},

  data: () => ({
    responseResult: {},
    confirmationSuccess: null,
    currentLang: null,
    errorMessage: '',
    mode: 'verify-link'
  }),
  mounted() {
    this.setLocalizedTitle()
    this.loadOrganizationInfo()
  },
  async created () {
    this.currentLang = this.$i18n?.locale || 'en'
    this.mode = this.$route.meta.mode || 'verify-link'
    await this.reVerify()
  },
  methods: {
    ...mapActions(['verifyLink', 'confirmInvite', 'fetchVerifyLinkInfo', 'fetchConfirmInviteInfo']),
    setLocalizedTitle () {
      document.title = this.$t('titles.verifyLink')
    },
    async loadOrganizationInfo () {
      const token = this.$route.params.token
      const userId = this.$route.params.userId || this.$route.params.id

      try {
        let orgResult
        if (this.mode === 'confirm-invite') {
          orgResult = await this.fetchConfirmInviteInfo({ userId, token })
        } else {
          orgResult = await this.fetchVerifyLinkInfo({ userId, token })
        }

        this.responseResult = orgResult?.organization || null
      } catch (error) {
        console.error('Failed to load organization info:', error)
      }
    },
    async reVerify () {
      try {
        const token = this.$route.params.token
        const userId = this.$route.params.userId || this.$route.params.id

        let result

        if (this.mode === 'confirm-invite') {
          result = await this.confirmInvite({ userId, token })
        } else {
          result = await this.verifyLink({ userId, token })
        }
        this.confirmationSuccess = result?.status === 200

        if (this.confirmationSuccess === false) {
          this.errorMessage = result?.data?.message || this.$t('messages.defaultError')
        } else {
          await new Promise(resolve => setTimeout(resolve, 1000))
          this.$router.push('/').catch(() => {})
        }
      } catch (error) {
        console.error('Verification failed:', error)
        this.confirmationSuccess = false
      }
    },
    updateLanguage (lang) {
      if (this.$i18n.locale !== lang) {
        this.$i18n.locale = lang
        this.currentLang = lang
        localStorage.setItem('locale', this.$i18n.locale)
      }
    }
  },
  watch: {
    currentLang (newLang, oldLang) {
      if (newLang !== oldLang && this.confirmationSuccess === false) {
        this.reVerify()
      }
    },
    '$i18n.locale' () {
      this.setLocalizedTitle()
    }
  }
}
</script>

<style lang="scss" scoped>
.verify-link {

  &__body {
    width: 100%;
    margin-top: 150px;

    .title {
      font-weight: 600;
      font-size: 28px;
      line-height: 38px;
      color: #27272E;
      margin-top: 15px;
    }

    .subtitle {
      font-weight: 600;
      font-size: 20px;
      line-height: 27px;
      color: #718096;
      margin-top: 30px;
    }
  }

  .verify-link__area,
  .bg-area {
    width: 50%;
    height: 100vh;
  }

  .verify-link__area {
    position: relative;
    background-color: #FFFFFF;

    &-wrapper {
      width: 424px;
      padding: 20px 0;
      margin: 60px auto 0;
    }

    .language-switcher {
      position: absolute;
      top: 20px;
      right: 20px;
      display: flex;
      column-gap: 2px;
      border-radius: 8px;
      background-color: #F7FAFC;
      padding: 2px;
      margin-left: auto;

      .button {
        ::v-deep .v-btn {
          width: 88px;
          color: #425466 !important;
          font-weight: 600 !important;
          font-size: 12px;
          line-height: 1;
          background-color: #F7FAFC !important;
          border-radius: 8px;
          padding: 9px 8px;
        }
      }
      .button.active-language {
        ::v-deep .v-btn {
          color: white !important;
          background-color: #008C89 !important;
        }
      }
    }
  }

  .bg-area {
    width: 50%;
    height: 100vh;
    position: relative;
    background-color: $dark-green;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(151.33deg, #04B8B0 0%, rgba(4, 184, 176, 0) 43.71%);
      z-index: 1;
      pointer-events: none;
    }

    .bg-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .logo {
    ::v-deep {
      .v-image {
        border-radius: 50% !important;
      }
    }
  }
}

.back-to-login {
  position: relative;
  display: inline-flex;
  color: #A0AEC0;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  padding-top: 19px;
  margin: 20px 0 0;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    bottom: 6px;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: #A0AEC0;
  }
}

.spinner {
  width: 48px;
  height: 48px;
}

.rotating {
  -webkit-animation: rotating 1s linear infinite;
  -moz-animation: rotating 1s linear infinite;
  -ms-animation: rotating 1s linear infinite;
  -o-animation: rotating 1s linear infinite;
  animation: rotating 1s linear infinite;
}

@-webkit-keyframes rotating
  /* Safari and Chrome */ {
  from {
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@keyframes rotating {
  from {
    -ms-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -ms-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@media (max-width: 960px) {
  .verify-link__area {
    width: 100% !important;
    padding: 0 30px;
  }

  .bg-area {
    display: none;
  }
}

@media screen and (max-width: 500px) {
  .verify-link {
    .verify-link__area {
      flex-direction: column;
      justify-content: flex-start !important;
      padding: 0 15px;

      .language-switcher {
        position: relative;
        right: 0;
      }

      &-wrapper {
        width: 100%;
        margin-top: 30px;
      }
    }

    &__body {
      margin-top: 70px;
    }
  }
}
</style>

